import { useContext } from 'react'

import { useForm, useLatestEntity } from '@domain-group/react-hooks'
import type { UseFormOptions, UseFormPayload as UseFormBasePayload } from '@domain-group/react-hooks'

import { mergeAndStringifyEntityObjects } from '@functions/entity'

import type { AppDispatch } from '@redux/store'

import useDispatch from '@hooks/useDispatch'
import useSelector from '@hooks/useSelector'

import PageContext from '@contexts/pageContext'

import * as paginationRuleActions from '@redux/modules/paginationRule'
import type { ActionResult, EntityOptions } from '@redux/modules/types'

import defaultFormState, { requiredFields } from '@models/paginationRule'
import type { BrandModel, PaginationRuleModel } from '@models/types'

export const generateUrls = (paginationRule: Partial<PaginationRuleModel> = {}) => {
  const { id } = paginationRule
  return {
    editPaginationRuleUrl: `#/systemManager/paginationRules/${id}/edit`,
    paginationRulesIndexUrl: '#/systemManager/paginationRules',
  }
}

type CreatePaginationRulePayload = {
  paginationRuleParams: PaginationRuleModel,
  dispatch: AppDispatch,
  requestOptions?: EntityOptions,
}

const createPaginationRule = (params: CreatePaginationRulePayload) => {
  const { paginationRuleParams, dispatch, requestOptions } = params
  const { createPaginationRule: createFn } = paginationRuleActions

  const updatedParams = mergeAndStringifyEntityObjects({}, paginationRuleParams, [], ['sort_items_by'])

  return dispatch(createFn(updatedParams, requestOptions)).then((response) => {
    const { success } = response

    if (success){
      const { data } = response
      const { editPaginationRuleUrl } = generateUrls(data)

      return {
        success,
        data,
        redirectUrl: editPaginationRuleUrl,
      }
    }

    return response
  })
}

type UpdatePaginationRulePayload = {
  paginationRule: PaginationRuleModel,
  paginationRuleParams: PaginationRuleModel,
  dispatch: AppDispatch,
  requestOptions?: EntityOptions,
}

const updatePaginationRule = (params: UpdatePaginationRulePayload) => {
  const {
    paginationRule, paginationRuleParams, dispatch, requestOptions,
  } = params
  const { updatePaginationRule: updateFn } = paginationRuleActions

  const updatedParams = mergeAndStringifyEntityObjects(paginationRule, paginationRuleParams, [], ['sort_items_by'])

  return dispatch(updateFn(updatedParams, requestOptions))
}

type DeletePaginationRulePayload = {
  paginationRule: PaginationRuleModel,
  dispatch: AppDispatch,
}

const deletePaginationRule = (params: DeletePaginationRulePayload) => {
  const { dispatch, paginationRule } = params
  const { deletePaginationRule: deleteFn } = paginationRuleActions

  return dispatch(deleteFn(paginationRule)).then((response) => {
    const { success } = response

    if (success){
      const { paginationRulesIndexUrl } = generateUrls()

      return {
        ...response,
        redirectUrl: paginationRulesIndexUrl,
      }
    }

    return response
  })
}

type CreateOrEditPaginationRulePayload = {
  paginationRule: PaginationRuleModel,
  showCreateOrEditPaginationRuleModal: Function,
}

const createOrEditPaginationRule = (params: CreateOrEditPaginationRulePayload) => new Promise((resolve, reject) => {
  const { paginationRule, showCreateOrEditPaginationRuleModal } = params

  if (showCreateOrEditPaginationRuleModal){
    const payload = {
      paginationRule,
    }

    showCreateOrEditPaginationRuleModal(payload)

    resolve({ success: true, result: payload })
  }

  reject(new Error('showCreateOrEditPaginationRuleModal not defined in PageContext callbacks'))
})

export type UseFormPayload = UseFormBasePayload & {
  creating?: boolean,
  updating?: boolean,
}

type CustomFormOptions = {
  customRequiredFields?: UseFormOptions['requiredFields'],
}

export function usePaginationRuleForm(
  paginationRule: Partial<PaginationRuleModel> = {},
  options: UseFormOptions & CustomFormOptions = {},
): UseFormPayload {
  const { customRequiredFields = [], validateOn } = options

  const { creating, updating } = useSelector(reduxState => reduxState.paginationRules)

  const paginationRuleForm = useForm(
    defaultFormState,
    { entity: paginationRule, requiredFields: [...requiredFields, ...customRequiredFields], validateOn },
    [paginationRule.id, paginationRule.cache_key],
  )

  return {
    ...paginationRuleForm,
    creating,
    updating,
  }
}

type UseRelationsPayload = {
  brand: Partial<BrandModel>,
}

export function useRelations(paginationRule: Partial<PaginationRuleModel> = {}): UseRelationsPayload {
  const { brand_id } = paginationRule

  const brands = useSelector(reduxState => reduxState.entities.brands)

  const brand: Partial<BrandModel> = brand_id ? brands[brand_id] || {} : {}

  return {
    brand,
  }
}

export type UsePaginationRulePayload = {
  callbacks: {
    createPaginationRule: (paginationRuleParams: PaginationRuleModel, requestOptions: EntityOptions) => (
      Promise<ActionResult<PaginationRuleModel>>
    ),
    createOrEditPaginationRule: () => void,
    deletePaginationRule: () => void,
    updatePaginationRule: (paginationRuleParams: PaginationRuleModel, requestOptions: EntityOptions) => (
      Promise<ActionResult<PaginationRuleModel>>
    ),
  },
  creating: boolean,
  loading: boolean,
  paginationRule: PaginationRuleModel,
  updating: boolean,
  urls: ReturnType<typeof generateUrls>,
}

function usePaginationRule(initEntity: Partial<PaginationRuleModel> = {}): UsePaginationRulePayload {
  const { entity: paginationRule } = useLatestEntity(initEntity, 'paginationRules')

  const dispatch = useDispatch()

  const { callbacks } = useContext(PageContext)
  const { showCreateOrEditPaginationRuleModal } = callbacks || {}

  const { creating, loading, updating } = useSelector(reduxState => reduxState.paginationRules)

  return {
    callbacks: {
      createPaginationRule: (
        paginationRuleParams: PaginationRuleModel,
        requestOptions: EntityOptions,
      ) => createPaginationRule({ paginationRuleParams, dispatch, requestOptions }),
      createOrEditPaginationRule: () => (
        // @ts-expect-error TS2322
        createOrEditPaginationRule({ paginationRule, showCreateOrEditPaginationRuleModal })
      ),
      // @ts-expect-error TS2322
      deletePaginationRule: () => deletePaginationRule({ paginationRule, dispatch }),
      updatePaginationRule: (
        paginationRuleParams: PaginationRuleModel,
        requestOptions: EntityOptions,
      ) => updatePaginationRule({
        // @ts-expect-error TS2322
        paginationRule, paginationRuleParams, dispatch, requestOptions,
      }),
    },
    creating,
    loading: !!loading,
    // @ts-expect-error TS2322
    paginationRule,
    updating,
    urls: generateUrls(paginationRule),
  }
}

export default usePaginationRule
